/**
 * Prefer server message from API error bodies (OAuth error_description, ProblemDetails,
 * ASP.NET Identity errors, ErrorResponseViewModel, etc.).
 *
 * Supports both raw axios errors (`error.response.data`) and RTK Query / unwrap
 * (`error.data`).
 */
export const PRICING_EXPIRED_MESSAGE = 'Pricing has expired.';

export function getAxiosErrorMessage(error, fallback = 'Something went wrong.') {
  const data = error?.data ?? error?.response?.data;

  if (data == null || data === '') {
    const msg = error?.message;
    if (typeof msg === 'string' && !/^Request failed with status code \d+$/.test(msg)) {
      return sanitizeClientErrorMessage(msg) || fallback;
    }
    return fallback;
  }

  if (typeof data === 'string') {
    return sanitizeClientErrorMessage(data) || fallback;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return fallback;
    const parts = data.map((item) => {
      if (item == null) return '';
      if (typeof item === 'string') return item;
      if (typeof item === 'object') {
        return item.description ?? item.message ?? '';
      }
      return String(item);
    }).filter(Boolean);
    if (parts.length > 0) return sanitizeClientErrorMessage(parts.join(' ')) || fallback;
    return fallback;
  }

  if (typeof data === 'object' && data !== null) {
    if (data.error_description != null && data.error_description !== '') {
      return sanitizeClientErrorMessage(String(data.error_description)) || fallback;
    }
    if (data.ErrorMessage != null && data.ErrorMessage !== '') {
      return sanitizeClientErrorMessage(String(data.ErrorMessage)) || fallback;
    }
    if (data.detail != null && data.detail !== '') {
      return sanitizeClientErrorMessage(String(data.detail)) || fallback;
    }
    if (data.message != null && data.message !== '') {
      return sanitizeClientErrorMessage(String(data.message)) || fallback;
    }
    if (data.errorMessage != null && data.errorMessage !== '') {
      return sanitizeClientErrorMessage(String(data.errorMessage)) || fallback;
    }
    if (data.title != null && data.title !== '') {
      return sanitizeClientErrorMessage(String(data.title)) || fallback;
    }
    if (data.error != null && data.error !== '') {
      return sanitizeClientErrorMessage(String(data.error)) || fallback;
    }
  }

  return fallback;
}

export function sanitizeClientErrorMessage(message) {
  if (typeof message !== 'string') return message;
  const trimmed = message.trim();
  if (!trimmed) return trimmed;
  if (isExpiredPricingMessage(trimmed)) return PRICING_EXPIRED_MESSAGE;

  const fromDump = extractNswagResponseBody(trimmed);
  if (fromDump) {
    if (isExpiredPricingMessage(fromDump)) return PRICING_EXPIRED_MESSAGE;
    return fromDump;
  }

  return trimmed;
}

function isExpiredPricingMessage(text) {
  return /pricing/i.test(text) && /expired/i.test(text);
}

/** NSwag concatenates the OpenAPI 400 description with Status/Response. Prefer the body. */
function extractNswagResponseBody(message) {
  if (!/Status:\s*\d+/i.test(message) || !/Response:/i.test(message)) return null;
  const match = message.match(/Response:\s*"?([\s\S]*?)"?\s*$/i);
  if (!match) return null;
  const body = match[1].trim();
  if (!body || body === '(null)') return null;
  try {
    const json = JSON.parse(body);
    if (typeof json === 'string') return json.trim();
    if (json && typeof json === 'object') {
      const nested = json.message || json.error || json.title || json.detail || json.ErrorMessage;
      if (nested) return String(nested).trim();
    }
  } catch {
    // LOS often returns a plain-text sentence, not JSON.
  }
  return body;
}
